import React from 'react';
import TextArea from '../Form/TextArea';
import Button from '../Form/Button';
import { LetterLayout, buildLetterHeaderHtml, buildLetterFooterHtml } from './PersonnelLetterTemplate';

interface LetterPreviewPanelProps {
  showPreview: boolean;
  isEditable: boolean;
  previewContent: string;
  editableContent: string;
  onEditableChange: (value: string) => void;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onPrint: () => void;
  emptyIcon: React.ReactNode;
  emptyMessage: string;
  /** Letterhead/footer of the selected template — shown around the body so the preview matches the print. */
  layout?: LetterLayout;
}

const LetterPreviewPanel: React.FC<LetterPreviewPanelProps> = ({
  showPreview,
  isEditable,
  previewContent,
  editableContent,
  onEditableChange,
  onEdit,
  onSave,
  onCancel,
  onPrint,
  emptyIcon,
  emptyMessage,
  layout
}) => {
  const headerHtml = buildLetterHeaderHtml(layout);
  const footerHtml = buildLetterFooterHtml(layout, false);
  return (
    <div className="space-y-4">
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Letter Preview</h3>
          {showPreview && (
            <div className="flex space-x-2">
              {!isEditable ? (
                <>
                  <Button type="button" variant="secondary" onClick={onEdit} className="text-sm">
                    Edit
                  </Button>
                  <Button type="button" variant="secondary" onClick={onPrint} className="text-sm">
                    Print
                  </Button>
                </>
              ) : (
                <>
                  <Button type="button" variant="primary" onClick={onSave} className="text-sm">
                    Save
                  </Button>
                  <Button type="button" variant="secondary" onClick={onCancel} className="text-sm">
                    Cancel
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        {showPreview ? (
          <div className="border border-gray-300 rounded-lg p-4 bg-gray-50 min-h-96">
            {layout && <div dangerouslySetInnerHTML={{ __html: headerHtml }} />}
            {isEditable ? (
              <TextArea
                value={editableContent}
                onChange={(e) => onEditableChange(e.target.value)}
                rows={15}
                className="w-full text-sm font-mono"
              />
            ) : (
              <div
                className="text-gray-700 text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: previewContent }}
              />
            )}
            {layout && <div dangerouslySetInnerHTML={{ __html: footerHtml }} />}
          </div>
        ) : (
          <div className="border border-gray-300 rounded-lg p-8 bg-gray-50 text-center text-gray-500">
            {emptyIcon}
            <p>{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LetterPreviewPanel;
